import {registerBrick, QueryResult, CloudObject, Color as ColorAPI} from 'olympe';
import {
    ReactBrick,
    ifNotTransparent,
    useProperty,
    getColorDefinition,
    ifNotNull,
    jsonToSxProps,
    cssToSxProps,
    getLogger,
} from '@olympeio/core';
import React, {useRef} from 'react';
import {merge, of} from 'rxjs';
import {switchMap, map} from 'rxjs/operators';

import {Chart} from 'chart.js';
import Box from '@mui/material/Box';

export default class SpeedometerChart extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return $.observe('Hidden');
    }

    /**
     * Subscribe to the data as well to fill the chart js object with the data
     * (This last step is done in the processData method)
     * Finally set the context variable 'graph data' with the chart js dataset object
     *
     * By overriding init() we can set the context variable used in the rendering of the React component
     * and we don't need to override the setupExecution() since Visual Component are rendered only once
     * and then react takes care of further updates
     *
     * @override
     */
    init($) {
        this.logger = getLogger('SpeedometerChart');

        $.observe('data', false)
            .pipe(
                switchMap((data) => {
                    if (!data) {
                        return of([]);
                    } else if (data instanceof QueryResult) {
                        return of(data.toArray());
                    } else if (Array.isArray(data)) {
                        return of(data);
                    } else {
                        this.logger.error(
                            'The provided data is neither a list/array nor an QueryResult.'
                        );
                        return of([]);
                    }
                }),

                // Put the trigger to redisplay anytime any data prop change, also when the user specify manually to refresh the chart
                switchMap((data) => {
                    return merge(
                        $.observe('Refresh', false).pipe(map(() => data)),
                        $.observe('font family', false).pipe(map(() => data)),
                        $.observe('font size', false).pipe(map(() => data)),
                        $.observe('percentage font size', false).pipe(map(() => data)),
                        $.observe('value path', false).pipe(map(() => data)),
                        $.observe('color path', false).pipe(map(() => data)),
                        $.observe('label path', false).pipe(map(() => data))
                    );
                }),
                // Add data to the configs to create the datasets
                map((data) => this.processData($, data))
            )
            .subscribe((datasets) => {
                $.set('graph data', datasets);
            });
    }

    /**
     * Mix the data and the config into the dataset structure we send to chart js
     *
     * @private
     * @param {BrickContext} $
     * @param {Map | Array} data
     * @return {Object}
     */
    processData($, data) {
        const result = {
            data: [],
            backgroundColor: [],
            labels: [],
        };

        // Do the sanity checks over the data before injecting it into the chart config object
        if (data && data.length > 0) {
            const valueProperty = $.get('value path');
            const colorProperty = $.get('color path');
            const labelProperty = $.get('label path');

            // Injecting the data in the chart js
            for (let i = 0; i < data.length; i++) {
                let item = data[i];
                if (item instanceof CloudObject) {
                    item = item.toJSON();
                }
                let color;
                if (item[colorProperty] instanceof ColorAPI) {
                    color = getColorDefinition(item[colorProperty], 'Speedometer Chart');
                } else if (typeof item[colorProperty] === 'string') {
                    color = item[colorProperty];
                } else {
                    this.logger.error(
                        `Invalid color provided. Must be of type Color or string: ${item[colorProperty]}`
                    );
                }
                result.data.push(item[valueProperty]);
                result.backgroundColor.push(color);
                result.labels.push(item[labelProperty]);
            }

            return {
                labels: result.labels,
                datasets: [
                    {
                        data: result.data,
                        backgroundColor: result.backgroundColor,
                        borderColor: ['black'],
                        borderWidth: 1,
                        cutout: '80%',
                        circumference: 180,
                        rotation: 270,
                    },
                ],
            };
        } else {
            return {
                labels: ['red', 'yellow', 'green'],
                datasets: [
                    {
                        data: [33, 33, 33],
                        backgroundColor: [
                            'rgba(255, 26, 104, 0.2)',
                            'rgba(255, 206, 86, 0.2)',
                            'rgba(75, 192, 192, 0.2)',
                        ],
                        borderColor: ['black'],
                        borderWidth: 1,
                        cutout: '80%',
                        circumference: 180,
                        rotation: 270,
                    },
                ],
            };
        }
    }

    /**
     * Drawing the component itself
     *
     * @override
     * @param {BrickContext} $
     */
    static getReactComponent($) {
        return (props) => {
            const canvasRef = useRef(null);
            const [chart, setChart] = React.useState(null);
            const [hidden] = props.values;
            const logger = getLogger('SpeedometerChart');

            const title = useProperty($, 'title', true);
            const percentage = useProperty($, 'percentage', true);
            const displayPercentage = useProperty($, 'display percentage');
            const needleColor = useProperty($, 'needle color');
            const displayNeedle = useProperty($, 'display needle', true);
            const data = useProperty($, 'graph data', true);
            const legendPosition = useProperty($, 'legend position', true);
            const tooltipMode = useProperty($, 'tooltip mode', true);

            // Build the whole configuration object if there is any change
            React.useEffect(() => {
                const fontSize = $.get('font size');
                const fontFamily = $.get('font family');
                const percentageFontSize = $.get('percentage font size');

                // check if props are ready
                const canvas = canvasRef.current;
                if (!canvas || !data) return;

                let valuePercentage = 0;

                if (percentage < 0 || percentage > 100) {
                    logger.warn('The percentage value should be between 0 - 100');
                } else {
                    valuePercentage = percentage;
                }

                const calculatePosition = (legendPosition, chartWidth, legendWidth) => {
                    if (legendPosition === 'left') {
                        return chartWidth + legendWidth;
                    }
                    return chartWidth;
                };

                // gaugeNeedle block
                const gaugeNeedle = {
                    id: 'gaugeNeedle',
                    afterDatasetDraw(chart) {
                        const {
                            ctx,
                            config,
                            chartArea: {top, bottom, left, right, width, height},
                        } = chart;
                        ctx.save();
                        const legendWidth = chart.legend.width;
                        const dataTotal = data.datasets[0].data.reduce((a, b) => a + b, 0);
                        const needleValue = (dataTotal * valuePercentage) / 100;
                        const calculatedPosition = calculatePosition(
                            legendPosition,
                            width / 2,
                            legendWidth
                        );
                        const angle = Math.PI + (needleValue / dataTotal) * Math.PI; // calculate the angle
                        const meta = chart.getDatasetMeta(0);
                        const innerRadius = meta.controller.innerRadius;

                        const cx = calculatedPosition;
                        const cy = chart._metasets[0].data[0].y;

                        ctx.translate(cx, cy);
                        ctx.rotate(angle);
                        // Needle
                        ctx.beginPath();
                        ctx.strokeStyle = getColorDefinition(needleColor, 'Speedometer Chart');
                        ctx.fillStyle = getColorDefinition(needleColor, 'Speedometer Chart');
                        ctx.moveTo(0, -5);
                        ctx.lineTo(innerRadius, 0); // Define the height of the needle
                        ctx.lineTo(0, 5);
                        ctx.stroke();
                        ctx.fill();
                        // dot
                        ctx.beginPath();
                        ctx.arc(0, 0, 5, 0, Math.PI * 2, false);
                        ctx.fill();
                        ctx.restore();
                    },
                };

                // percentage block
                const gaugePercentage = {
                    id: 'gaugePercentage',
                    afterDatasetDraw(chart) {
                        const {
                            ctx,
                            config,
                            chartArea: {top, bottom, left, right, width, height},
                        } = chart;

                        ctx.save();

                        const needleValue = valuePercentage;
                        const legendWidth = chart.legend.width;

                        const calculatedPosition = calculatePosition(
                            legendPosition,
                            width / 2,
                            legendWidth
                        );
                        const yCenter = chart._metasets[0].data[0].y;                        

                        const yAxis = displayNeedle ?  yCenter + percentageFontSize + 10 : yCenter;

                        // Percentage
                        ctx.save();
                        ctx.font = `${percentageFontSize}px ${fontFamily}`;
                        ctx.fillStyle = getColorDefinition(needleColor, 'Speedometer Chart');
                        ctx.fillText(
                            needleValue + '%',
                            calculatedPosition - percentageFontSize,
                            yAxis
                        );
                        ctx.textAlign = 'center';
                        ctx.restore();
                    },
                };

                const config = {
                    type: 'doughnut',
                    data,
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                display: legendPosition !== 'none',
                                position: legendPosition,
                                fullSize: false,
                                labels: {
                                    font: {
                                        size: fontSize,
                                        family: fontFamily,
                                    },
                                },
                            },
                            title: {
                                display: title !== null && title !== undefined && title !== '',
                                text: String(title),
                                font: {
                                    size: fontSize,
                                    family: fontFamily,
                                },
                            },
                            datalabels: false,
                            tooltip: {
                                enabled: tooltipMode !== 'none',
                                mode: tooltipMode,
                            },
                        },
                        animation: false,
                    },
                    plugins: [
                        displayNeedle ? gaugeNeedle : {},
                        displayPercentage ? gaugePercentage : {},
                    ],
                };

                // When we want to draw the chart again, we need to destroy it first
                if (chart) {
                    chart.destroy();
                }

                setChart(new Chart(canvas, config));
            }, [
                title,
                percentage,
                displayPercentage,
                needleColor,
                displayNeedle,
                data,
                legendPosition,
                tooltipMode,
            ]);

            return (
                !hidden && (
                    <Box
                        // Properties + UI
                        component={'div'}
                        sx={{
                            width: 1,
                            height: 1,
                            ...ifNotTransparent('borderColor', useProperty($, 'Border Color')),
                            ...ifNotNull(
                                'borderRadius',
                                `${useProperty($, 'Border Radius')}px`,
                                useProperty($, 'Border Radius')
                            ),
                            borderWidth: useProperty($, 'Border Width'),
                            borderStyle: 'solid',
                            boxSizing: 'border-box',
                            ...ifNotTransparent('backgroundColor', useProperty($, 'Default Color')),
                            ...cssToSxProps(useProperty($, 'CSS Property')),
                            ...jsonToSxProps(useProperty($, 'MUI sx [json]')),
                        }}
                        onClick={SpeedometerChart.clickHandler.bind(this, $, chart)}>
                        <canvas ref={canvasRef}></canvas>
                    </Box>
                )
            );
        };
    }

    /**
     * Triggers the on item click if the function is defined. if on item click is triggered, on click is not.
     * If there is no on item click function defined, on click is triggered all the time
     *
     * @param {BrickContext} $ the olympe context
     * @param {Chart} chart the chart js object used to compute where the user clicked
     * @param {React.MouseEvent} evt the html event on click
     */
    static clickHandler($, chart, evt) {
        const lambda = $.get('On Item Click');
        if (lambda !== null) {
            const activePoints = chart.getElementsAtEventForMode(
                evt,
                'nearest',
                {intersect: true},
                false
            );
            if (activePoints.length > 0) {
                const [{datasetIndex, index}] = activePoints;
                let inputData = $.get('data');
                let clickedData;
                if (Array.isArray(inputData) || inputData instanceof QueryResult) {
                    if (inputData instanceof QueryResult) {
                        inputData = inputData.toArray();
                    }
                    clickedData = inputData[index];
                }
                const [ctrlFlow, data] = lambda.getInputs();
                $.runner(lambda).set(data, clickedData).trigger(ctrlFlow);
            } else {
                $.trigger('On Click');
            }
        } else {
            $.trigger('On Click');
        }
    }
}

registerBrick('018deeb84c8e3bd10e13', SpeedometerChart);
